import { useEffect, useMemo, useState } from 'react'
import { History, Info } from 'lucide-react'
import { label } from './species-names'
import './backtest-panel.css'

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'máj', 'jún', 'júl', 'aug', 'sep', 'okt', 'nov', 'dec']

function decimal(value, digits = 2) {
  return value == null ? '—' : value.toFixed(digits).replace('.', ',')
}

function percent(value) {
  return value == null ? '—' : `${Math.round(value * 100)} %`
}

// The replay only covers days somebody reported from the forest, and those are
// scattered through the season. Drawing them on a real calendar axis keeps the
// gaps visible instead of squeezing them out.
function SeasonChart({ item }) {
  const series = item.series
  const [start, end] = useMemo(() => {
    const year = Number(series[0][0].slice(0, 4))
    return [Date.UTC(year, 0, 1), Date.UTC(year, 11, 31)]
  }, [series])

  const width = 1000
  const height = 116
  const padLeft = 30
  const padRight = 6
  const top = 8
  const areaBottom = 78
  const tickBase = height - 20
  const plot = width - padLeft - padRight
  const dayAt = (iso) => (Date.parse(`${iso}T00:00:00Z`) - start) / 86400000
  const total = (end - start) / 86400000
  const x = (iso) => padLeft + (dayAt(iso) / total) * plot
  const y = (value) => top + (areaBottom - top) - value * (areaBottom - top)

  const first = series[0][0]
  const last = series[series.length - 1][0]
  const line = series.map(([date, prediction]) => `${x(date).toFixed(1)},${y(prediction).toFixed(1)}`).join(' ')

  const months = MONTHS.map((name, index) => {
    const iso = `${first.slice(0, 4)}-${String(index + 1).padStart(2, '0')}-01`
    return { name, at: x(iso) }
  }).filter((month) => month.at >= x(first) - 12 && month.at <= x(last) + 12)

  return (
    <svg className="bt-chart" viewBox={`0 0 ${width} ${height}`} role="img"
      aria-label={`Denná predpoveď pre skupinu ${label(item.group).name} a nahlásená skutočnosť`}>
      {[0.5, 1].map((value) => (
        <g key={value}>
          <line className="bt-grid" x1={padLeft} x2={width - padRight} y1={y(value)} y2={y(value)} />
          <text className="bt-axis" x="0" y={y(value) + 4}>{value * 100}%</text>
        </g>
      ))}
      {months.map((month) => (
        <g key={month.name}>
          <line className="bt-month" x1={month.at.toFixed(1)} x2={month.at.toFixed(1)} y1={top} y2={tickBase} />
          <text className="bt-axis" x={(month.at + 4).toFixed(1)} y={height - 3}>{month.name}</text>
        </g>
      ))}
      <polygon className="bt-area"
        points={`${x(first).toFixed(1)},${y(0).toFixed(1)} ${line} ${x(last).toFixed(1)},${y(0).toFixed(1)}`} />
      <polyline className="bt-line" points={line} />
      {series.map(([date, prediction, actual]) => (
        <line key={date} className={actual ? 'bt-hit' : 'bt-quiet'}
          x1={x(date).toFixed(1)} x2={x(date).toFixed(1)}
          y1={tickBase - (actual ? 16 : 5)} y2={tickBase}>
          <title>
            {date} — {actual ? 'skupinu hlásili' : 'hlásenia bez tejto skupiny'}
            {`, predpoveď ${Math.round(prediction * 100)} %`}
          </title>
        </line>
      ))}
    </svg>
  )
}

// "When the model said 60, how often was the group actually reported?" Four
// bins of predicted probability against the observed rate in each. A model
// whose bars climb from left to right is telling the truth about its own
// confidence; one whose bars are flat is only shuffling the calendar.
const CALIBRATION_BINS = [[0, 0.25, '0–25'], [0.25, 0.5, '25–50'], [0.5, 0.75, '50–75'], [0.75, 1.01, '75–100']]

function CalibrationStrip({ series, baseRate }) {
  const bins = CALIBRATION_BINS.map(([low, high, name]) => {
    const rows = series.filter(([, prediction]) => prediction >= low && prediction < high)
    const hits = rows.filter(([, , actual]) => actual).length
    return { name, count: rows.length, rate: rows.length ? hits / rows.length : null }
  })
  if (!bins.some((bin) => bin.count >= 5)) return null
  return (
    <div className="bt-calibration" role="img" aria-label="Kalibrácia: podiel skutočných nálezov v každom pásme predpovede">
      <span className="bt-calibration-title">Keď model povedal…</span>
      {bins.map((bin) => (
        <div key={bin.name} className={`bt-cal-bin ${bin.count < 5 ? 'is-thin' : ''}`}
          title={bin.count ? `Predpoveď ${bin.name} %: skupinu hlásili v ${percent(bin.rate)} z ${bin.count} dní` : `Predpoveď ${bin.name} %: žiadny deň`}>
          <span className="bt-cal-name">{bin.name} %</span>
          <div className="bt-cal-track">
            <i className="bt-cal-base" style={{ left: `${Math.round(baseRate * 100)}%` }} />
            {bin.rate != null && <b style={{ width: `${Math.round(bin.rate * 100)}%` }} />}
          </div>
          <strong>{bin.count ? `${percent(bin.rate)} nálezov` : '—'}<small> · {bin.count} dní</small></strong>
        </div>
      ))}
      <span className="bt-calibration-note">čiarka = základná miera {percent(baseRate)}</span>
    </div>
  )
}

function GroupRow({ item }) {
  const copy = label(item.group)
  const beats = item.roc_auc != null && item.season_baseline_auc != null
    && item.roc_auc > item.season_baseline_auc
  const lift = item.base_rate > 0 ? item.top_day_hit_rate / item.base_rate : null
  return (
    <article className="bt-group">
      <div className="bt-group-head">
        <h3>{copy.name}</h3>
        <em>{copy.latin}</em>
        <span className="bt-spacer" />
        <span className={`bt-chip ${beats ? 'is-good' : 'is-weak'}`}>AUC {decimal(item.roc_auc)}</span>
        <span className="bt-chip">iba dátum {decimal(item.season_baseline_auc)}</span>
        <span className="bt-chip">{item.finds} nálezov / {item.days} dní</span>
      </div>
      <p className="bt-verdict">
        {beats
          ? `Počasie prekonalo kalendár. V ${item.top_days} dňoch s najvyšším skóre sedelo ${percent(item.top_day_hit_rate)} predpovedí, oproti základnej miere ${percent(item.base_rate)}${lift ? ` — ${decimal(lift, 1)}× viac` : ''}.`
          : `Počasie kalendár neprekonalo — o tejto skupine v tejto sezóne rozhodoval dátum, nie vlaha.`}
      </p>
      <SeasonChart item={item} />
      <CalibrationStrip series={item.series} baseRate={item.base_rate} />
    </article>
  )
}

export default function BacktestPanel() {
  const [data, setData] = useState(null)
  const [season, setSeason] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    fetch('/api/backtest')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('missing'))))
      .then((payload) => {
        setData(payload)
        setSeason(payload.years?.[0] ?? null)
      })
      .catch(() => setFailed(true))
  }, [])

  const groups = useMemo(() => {
    const rows = data?.seasons?.[season] || []
    return [...rows].sort((a, b) => (b.roc_auc ?? 0) - (a.roc_auc ?? 0))
  }, [data, season])

  if (failed || !data || !season) return null

  const beating = groups.filter((item) => item.roc_auc > item.season_baseline_auc).length
  const reporting = data.reporting?.[season]

  return (
    <section className="backtest-panel" id="spatny-test">
      <div className="section-heading bt-heading">
        <div>
          <span className="eyebrow"><History size={15} /> Spätný test</span>
          <h2>Ako to dopadlo v minulých sezónach</h2>
        </div>
        <div className="bt-years" aria-label="Vyber sezónu">
          {data.years.map((year) => (
            <button className={year === season ? 'active' : ''} key={year}
              onClick={() => setSeason(year)} type="button" aria-pressed={year === season}>
              {year}
            </button>
          ))}
        </div>
      </div>

      <p className="bt-intro">
        Každý deň nižšie je predpoveď z kroku, v ktorom bol{' '}
        <strong>celý rok {season} vynechaný z tréningu</strong> — model túto sezónu
        nikdy nevidel. Zelená plocha je
        jeho denná predpoveď, jantárové značky sú dni, keď skupinu naozaj hlásili,
        sivé sú dni, keď ľudia hlásili niečo iné.
      </p>

      <div className="bt-summary">
        <div><span>Sezóna</span><strong>{season}</strong></div>
        <div><span>Skupiny nad kalendárom</span><strong>{beating} z {groups.length}</strong></div>
        {reporting && (
          <>
            <div><span>Dní s hlásením</span><strong>{reporting.reporting_days}</strong></div>
            <div><span>Hlásení spolu</span><strong>{reporting.reports}</strong></div>
          </>
        )}
      </div>

      <div className="bt-legend">
        <span><i className="bt-key-pred" /> predpoveď modelu</span>
        <span><i className="bt-key-hit" /> skupinu v ten deň hlásili</span>
        <span><i className="bt-key-quiet" /> hlásili, ale túto skupinu nie</span>
      </div>

      <div className="bt-groups">
        {groups.map((item) => <GroupRow item={item} key={item.group} />)}
      </div>

      <div className="bt-note">
        <Info size={16} />
        <p>{data.evidence_note} {data.method}</p>
      </div>
    </section>
  )
}
